import { useEffect, useRef, useState, memo, useCallback } from 'react';

function Contact() {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('address', formData.address);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6514e1of4o2fjfo9bhg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }

      setSubmitStatus('success');
      setFormData({
        name: '',
        phone: '',
        email: '',
        address: '',
        message: '',
      });
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  }, [formData]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  return (
    <section id="contact" ref={sectionRef} className="py-16 sm:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`text-center mb-10 sm:mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="text-sky-600 text-lg sm:text-xl font-semibold mb-2">Get In Touch</p>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-3 sm:mb-4">
            Request Service or Assessment
          </h2>
          <p className="text-gray-600 text-xl sm:text-2xl max-w-3xl mx-auto">
            Experiencing property damage? Contact us now for a free assessment. Our team is available 24/7 for
            emergencies throughout Davis County and Northern Utah.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Contact info */}
          <div
            className={`order-2 lg:order-1 transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <div className="grid sm:grid-cols-2 lg:grid-cols-1 gap-4 sm:gap-6 lg:gap-8">
              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-xl">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">
                  Contact Us In Kaysville
                </h3>
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 bg-sky-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-base sm:text-lg text-sky-600"></i>
                    </div>
                    <div>
                      <p className="text-gray-700 text-base">854 W 450 N, STE 9</p>
                      <p className="text-gray-700 text-base">Kaysville, Utah 84037</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 bg-sky-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-base sm:text-lg text-sky-600"></i>
                    </div>
                    <a
                      href="tel:3853406632"
                      className="text-gray-700 hover:text-sky-600 transition-colors text-base"
                    >
                      (385) 340-6632
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-xl">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">
                  Contact Us In Clearfield
                </h3>
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 bg-sky-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-base sm:text-lg text-sky-600"></i>
                    </div>
                    <div>
                      <p className="text-gray-700 text-base">P.O. Box 160554</p>
                      <p className="text-gray-700 text-base">Clearfield, UT 84016</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 bg-sky-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-base sm:text-lg text-sky-600"></i>
                    </div>
                    <a
                      href="tel:3853406632"
                      className="text-gray-700 hover:text-sky-600 transition-colors text-base"
                    >
                      (385) 340-6632
                    </a>
                  </div>
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 bg-sky-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-base sm:text-lg text-sky-600"></i>
                    </div>
                    <a
                      href="mailto:info@daviscountydisastercleanup.com"
                      className="text-gray-700 hover:text-sky-600 transition-colors text-base"
                    >
                      info@daviscountydisastercleanup.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-sky-600 rounded-2xl p-6 sm:p-8 text-white mt-4 sm:mt-8">
              <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4">24/7 Emergency Service</h3>
              <p className="text-white/90 text-base mb-4">
                Disasters don't wait for business hours. We're available 24 hours a day, 7 days a week, 365 days a
                year.
              </p>
              <a
                href="tel:3853406632"
                className="inline-flex items-center gap-2 bg-white text-sky-600 px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold text-sm transition-all duration-200 hover:brightness-90 hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line text-lg"></i>
                Call (385) 340-6632
              </a>
            </div>

            <div className="mt-6 sm:mt-8 flex gap-3">
              <a
                href="https://www.facebook.com/Floodsmen-1107842726065270/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 sm:w-12 sm:h-12 bg-white rounded-lg flex items-center justify-center hover:bg-sky-600 hover:text-white transition-colors shadow-md cursor-pointer"
              >
                <i className="ri-facebook-fill text-lg sm:text-xl"></i>
              </a>
              <a
                href="http://yelp.com/biz/floodsmen-disaster-cleanup-clearfield-2"
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 sm:w-12 sm:h-12 bg-white rounded-lg flex items-center justify-center hover:bg-sky-600 hover:text-white transition-colors shadow-md cursor-pointer"
              >
                <i className="ri-star-smile-fill text-lg sm:text-xl"></i>
              </a>
            </div>
          </div>

          {/* Form */}
          <div
            className={`order-1 lg:order-2 transition-all duration-1000 delay-400 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <h4 className="text-sky-600 font-bold text-xl mb-4">Fill Out for Free Estimate:</h4>
            <form
              id="service-request-form"
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl p-6 sm:p-8 shadow-xl"
              data-readdy-form=""
            >
              <div className="space-y-4 sm:space-y-5">
                {/* Name */}
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-1.5"
                  >
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600 focus:border-transparent text-sm"
                    placeholder="John Smith"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-700 mb-1.5"
                  >
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600 focus:border-transparent text-sm"
                    placeholder="(801) 555-0123"
                  />
                </div>

                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-1.5"
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600 focus:border-transparent text-sm"
                    placeholder="john@example.com"
                  />
                </div>

                {/* Address */}
                <div>
                  <label
                    htmlFor="address"
                    className="block text-sm font-medium text-gray-700 mb-1.5"
                  >
                    Property Address
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600 focus:border-transparent text-sm"
                    placeholder="123 Main St, City, UT"
                  />
                </div>

                {/* Message */}
                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700 mb-1.5"
                  >
                    Describe Your Situation
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={3}
                    maxLength={500}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600 focus:border-transparent text-sm resize-none"
                    placeholder="Tell us about your situation..."
                  ></textarea>
                  <div className="text-xs text-gray-500 mt-1">
                    {formData.message?.length || 0}/500 characters
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-sky-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-sky-700 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {isSubmitting ? 'Sending...' : 'Request Free Assessment'}
                </button>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-sm">
                    Thank you! We'll contact you shortly to schedule your free assessment.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-sm">
                    Something went wrong. Please call us at (385) 340-6632 for immediate assistance.
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default memo(Contact);
